import moment from 'moment-timezone';
import pdfMake from 'pdfmake';
import { deepClone, readField, writeField } from "./objectUtilities";
import { makeCell, makeTitle} from './printingUtilities';
import { customFieldToString, defaultPrintValue } from '../components/CustomCtrl';
import { safeEval } from './safeEval';
import { generateDocx } from './docxUtilities';
import { ensureArray } from './arrayUtilities';
import { materialize } from './materializationUtilities';

export const CustomFormPrintingFactory = (realizationContext, lang, genDocDefinition) => {
    const { network, t, clientConfig: configuration } = realizationContext;
    const doFetch = async (template) => {
        if (network && typeof template === "string") {
            return await (await network.fetchData(template)).arrayBuffer();
        } else if (template instanceof File) {
            return await template.arrayBuffer();
        } else if (template instanceof ArrayBuffer) {
            return template;
        } else {
            throw new Error(`Unsupported template type (${template?.constructor?.name || typeof template}). Must be URL (string), File, or ArrayBuffer.`);
        }
    };
    const setDefaultValues = (data, initSpec) => {
        data = deepClone(data);
        for (const field of initSpec) {
            const value = readField(data, field.field);
            if ((value === null || value === undefined)) {
                writeField(data, field.field, defaultPrintValue(field));
            } else if (typeof value === 'boolean' || typeof value === 'number') {
                writeField(data, field.field, value.toString());
            } else {
                writeField(data, field.field, value);
            }
        }
        return data;
    }

    let self = {
        cellToPDF: async (cell, obj, buildProps) => {
            if (cell.cond) {
                if (!(safeEval(`(def, data, args) => ${cell.cond}`)(cell, obj, null))) {
                    return [];
                }
            }
            switch(cell.type) {
                case 'hidden':
                    return [];
                case 'static_image': {
                    const stack = [
                        { text: cell.label ? cell.label : ' ', style: 'formCellTitle' },
                    ];
                    const newCell = [{
                        stack
                    }];
                    stack.push({ image: cell.uri, margin: [15, 0, 0, 0], fit: [((400 * cell.colspan) / 12), 200] });
                    if (cell.colspan > 1) {
                        newCell[0].colSpan = cell.colspan;
                        for (let c = 1; c < cell.colspan; ++c) {
                            newCell.push({});
                        }
                    }
                    return newCell;
                }
                case 'signature':
                case 'image': {
                    const stack = [
                        { text: cell.label ? cell.label : ' ', style: 'formCellTitle' },
                    ];
                    const newCell = [{
                        stack
                    }];
                    let data = readField(obj, cell.field);
                    if (data) {
                        stack.push({ image: data, margin: [15, 0, 0, 0], fit: [((400 * cell.colspan) / 12), cell.type === 'signature' ? 25 : 200] });
                    } else {
                        stack.push({ text: ' ', alignment: 'left', style: 'data', margin: [15, 0, 0, 0] });
                    }
                    if (cell.colspan > 1) {
                        newCell[0].colSpan = cell.colspan;
                        for (let c = 1; c < cell.colspan; ++c) {
                            newCell.push({});
                        }
                    }
                    return newCell;
                }
                case 'table': {
                    let fieldValue = readField(obj, cell.field) || [];
                    let headerRow = cell.columns.map(c => ({ text: c.title, style: 'bold' }));
                    let bodyRows = [];
                    if (fieldValue.length > 0) {
                        for (const row of fieldValue) {
                            const toPush = [];
                            for (const col of cell.columns) {
                                const def = await self.cellToPDF({ ...col, label: col.title, colspan: 1 }, row, buildProps);
                                toPush.push(...def);
                            }
                            bodyRows.push(toPush);
                        }
                    } else {
                        bodyRows = [cell.columns.map((c, i) => i === 0 ? { text: 'No entries', colSpan: cell.columns.length } : {})];
                    }
                    let cells = [{
                        table: {
                            widths: cell.columns.map(() => '*'),
                            body: [headerRow].concat(bodyRows)
                        }
                    }];
                    let span = cell.colspan ?? 12;
                    if (span > 1) {
                        cells[0].colSpan = span;
                        for (let i = 1; i < span; ++i) {
                            cells.push({});
                        }
                    }
                    return cells;
                }
                case 'list': {
                    let fieldValue = readField(obj, cell.field) || [];
                    let hasEntries = fieldValue.length > 0;
                    let listBody = [];
                    if (hasEntries) {
                        listBody = await Promise.all(
                            fieldValue.map(async (row, i) => {
                                let tableContent = [];
                                for (let section of ensureArray(cell.rowdef)) {
                                    tableContent.push(...(await self.sectionToTable(realizationContext, section, row, buildProps)));
                                }
                                if (tableContent.length === 0) {
                                    const emptyRow = [{ text: ' ', colSpan: 12 }];
                                    for (let j = 1; j < 12; ++j) {
                                        emptyRow.push({});
                                    }
                                    tableContent.push(emptyRow);
                                }
                                return [
                                    { text: (i + 1) + ')', border: [false, false, false, false] },
                                    {
                                        table: {
                                            widths: ['*','*','*','*','*','*','*','*','*','*','*','*'],
                                            body: tableContent
                                        },
                                        border: [false, false, false, false]
                                    }
                                ];
                            })
                        );
                    }
                    let cells = [{
                        stack: [
                            { text: cell.label, style: 'formCellTitle' },
                            hasEntries
                                ? {
                                    table: {
                                        widths: ['auto','*'],
                                        body: listBody
                                    }
                                }
                                : { text: 'No entries' }
                        ]
                    }];
                    let span = cell.colspan ?? 12;
                    if (span > 1) {
                        cells[0].colSpan = span;
                        for (let i = 1; i < span; ++i) {
                            cells.push({});
                        }
                    }
                    return cells;
                }
                default: {
                    let value = await customFieldToString(cell, obj, realizationContext, '', lang);
                    return makeCell(cell.label, value, cell.colspan, buildProps?.(cell.field, obj));
                }
            }
        },
        sectionTableWidths: () => Array(12).fill(34.5),
        sectionToTable: async (realizationContext, section, obj, buildProps) => {
            if (section.cond && !(safeEval(`(section, data) => ${section.cond}`)(section, obj))) {
                return [];
            }
            section = await materialize(realizationContext, section);
            obj = await materialize(realizationContext, obj);
            let toReturn = [];
            if (!section.borderless) {
                if (section.title) {
                    toReturn.push(makeTitle(section.title, 12));
                }
            }
            if (section.rows) {
                for (let row of section.rows) {
                    let cells = [];
                    for (let cell of row) {
                        let pdfCell = await self.cellToPDF(cell, obj, buildProps);
                        cells.push(...pdfCell);
                    }
                    if (cells.length > 0) {
                        if (cells.length < 12) {
                            cells.push({ text: ' ', colSpan: 12 - cells.length });
                        }
                        for (let i = cells.length; i < 12; ++i) {
                            cells.push({});
                        }
                        if (!cells.every(cell => Object.keys(cell).length === 0)) {
                            toReturn.push(cells);
                        }
                    }
                }
            }
            return toReturn;
        },
        print: async (id, data, initSpec, template) => {
            if (initSpec) {
                data = setDefaultValues(data, initSpec);
            }
            return generateDocx(configuration, network, {
                template: await doFetch(template),
                data: { id, ...data },
                noSandbox: true,
                additionalJsContext: {
                    moment: function () { // TODO clean this up to use cdate (must first check it's not used in any of the docx templates)
                        return moment().tz(configuration.tz);
                    },
                }
            });
        },
        printForm: async function (id, uuid, form, formSpec, titleSuffix) {
            const input = ensureArray(form);
            if (formSpec.template) {
                const data = input.map(form => {
                    const initSpec = [];
                    formSpec.form.forEach(section => section.rows.forEach(row => row.forEach(cell => {
                        if (cell.field && (readField(input, cell.field) === null || readField(input, cell.field) === undefined)) {
                            initSpec.push(cell)
                        }
                    })));
                    return setDefaultValues(form, initSpec);
                });
                const dataToString = {};
                if (formSpec?.dataToString !== undefined) {
                    dataToString.dataToString = formSpec.dataToString;
                }
                if (Array.isArray(form)) {
                    return self.print(id, { data, ...dataToString }, null, formSpec.template);
                }
                return self.print(id, { ...data[0], ...dataToString }, null, formSpec.template);
            } else {
                let content = [];
                if (formSpec.type === 'Job') {
                    content.push({ text: t('customJobFormPrintTitle', {title: formSpec.title, id, uuid, suffix: (titleSuffix != null ? titleSuffix : '')}), style: 'header' });
                } else if (formSpec.type === 'Crew') {
                    content.push({ text: t('customCrewFormPrintTitle', {title: formSpec.title, id, uuid, suffix: (titleSuffix != null ? titleSuffix : '')}), style: 'header' });
                }
                for (const form of input) {
                    for (let i = 0; i < formSpec.form.length; i++) {
                        let section = formSpec.form[i];
                        let rows = await self.sectionToTable(realizationContext, section, form);
                        if (rows?.length > 0) {
                            let table = {
                                widths: this.sectionTableWidths(),
                                body: []
                            }
                            table.body = table.body.concat(rows);
                            let toAdd = [];
                            if (section.title) {
                                toAdd.push({ text: section.title });
                            } else if (i > 0) {
                                toAdd.push({ text: ' ' });
                            }
                            toAdd.push({ table: table });
                            content = content.concat(toAdd);
                        }
                    }
                }
                const data = await (new Promise((f, r) => {
                    pdfMake.createPdf(genDocDefinition(configuration, content), null, {
                        Roboto: {
                            normal: 'dejavusans.ttf',
                            bold: 'dejavusans-bold.ttf',
                            italics: 'dejavusans-oblique.ttf',
                            bolditalics: 'dejavusans-oblique.ttf'
                        }
                    }).getBuffer(buffer => f(buffer));
                }));
                return { data, mime: 'application/pdf', type: 'pdf' };
            }
        }
    }
    return self;
}